import React, { useRef } from "react";
import {
  Stack,
  TextInput,
  Textarea,
  NumberInput,
  Text,
  Paper,
  Group,
  Button,
  Anchor,
} from "@mantine/core";
import { DatePicker, TimeInput } from "@mantine/dates";
import { IconUpload, IconFile } from "@tabler/icons";
import moment from "moment";
import { showNotification } from "@mantine/notifications";
import { formatStepFieldLabel, isFileFieldType, isNumberFieldType, getStepFieldNumberPrecision, formatStepFieldNumberValue } from "../../data/mcaEntities";
import { useFormatter } from "../../hooks/useFomatter";

const ALLOWED_TYPES = [
  "application/pdf",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "video/mp4",
  "image/jpeg",
  "image/png",
];

const getFileUrl = (file) => {
  if (file?.fileUrl) return file.fileUrl;
  if (file?.fileLink) {
    const base = process.env.REACT_APP_FILE_URL ?? "";
    return `${base}${file.fileLink}`;
  }
  return null;
};

const parseDateValue = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const datePickerProps = {
  allowFreeInput: true,
  placeholder: "Pick date",
  zIndex: 1000,
};

const McaStepFieldsInput = ({
  fields = [],
  onChange,
  readOnly = false,
  canUpload = false,
  onUploadField,
  uploading = false,
}) => {
  const [formatter] = useFormatter();
  const inputRefs = useRef({});

  if (!fields.length) return null;

  const updateField = (idx, patch) => {
    onChange?.(
      fields.map((row, i) => (i === idx ? { ...row, ...patch } : row))
    );
  };

  const handleFile = async (fieldIdx, file) => {
    if (!file) return;
    if (file.type && !ALLOWED_TYPES.includes(file.type)) {
      showNotification({
        title: "Invalid file type",
        message: "Allowed: PDF, DOC, PPT, MP4, JPG, PNG",
        color: "red",
      });
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      showNotification({
        title: "File too large",
        message: "Maximum file size is 50MB",
        color: "red",
      });
      return;
    }
    await onUploadField?.(fieldIdx, file);
  };

  return (
    <Stack spacing="sm">
      {fields.map((field, idx) => {
        const label = `${formatStepFieldLabel(field)}${field.required ? " *" : ""}`;
        const inputType = field.inputType ?? "TEXT";

        if (readOnly) {
          if (isFileFieldType(inputType)) {
            const files = Array.isArray(field.value) ? field.value : [];
            return (
              <Text key={idx} size="sm">
                <Text component="span" weight={600}>
                  {formatStepFieldLabel(field)}:
                </Text>{" "}
                {files.length === 0
                  ? "—"
                  : files.map((file, fIdx) => {
                      const url = getFileUrl(file);
                      return (
                        <span key={fIdx}>
                          {fIdx > 0 ? ", " : ""}
                          {url ? (
                            <Anchor href={url} target="_blank" size="sm">
                              {file.fileName}
                            </Anchor>
                          ) : (
                            file.fileName
                          )}
                        </span>
                      );
                    })}
              </Text>
            );
          }
          return (
            <Text key={idx} size="sm">
              <Text component="span" weight={600}>
                {formatStepFieldLabel(field)}:
              </Text>{" "}
              {field.value === null || field.value === undefined || field.value === ""
                ? "—"
                : isNumberFieldType(inputType)
                  ? formatStepFieldNumberValue(
                      field.value,
                      getStepFieldNumberPrecision(field)
                    )
                  : inputType === "DATE"
                  ? moment(field.value).format("YYYY-MM-DD")
                  : inputType === "DATETIME"
                    ? moment(field.value).format("YYYY-MM-DD HH:mm")
                    : String(field.value)}
            </Text>
          );
        }

        if (isFileFieldType(inputType)) {
          const files = Array.isArray(field.value) ? field.value : [];
          return (
            <Paper key={idx} withBorder p="sm" radius="sm">
              <Group position="apart" mb={files.length ? "xs" : 0}>
                <Text size="sm" weight={500}>
                  {label}
                </Text>
                {canUpload && (
                  <>
                    <input
                      type="file"
                      hidden
                      ref={(el) => {
                        inputRefs.current[idx] = el;
                      }}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleFile(idx, file);
                        e.target.value = "";
                      }}
                    />
                    <Button
                      size="xs"
                      variant="light"
                      leftIcon={<IconUpload size={14} />}
                      loading={uploading}
                      onClick={() => inputRefs.current[idx]?.click()}
                    >
                      Upload
                    </Button>
                  </>
                )}
              </Group>
              {files.length === 0 ? (
                <Text size="xs" color="dimmed">
                  No files uploaded
                </Text>
              ) : (
                <Stack spacing={4}>
                  {files.map((file, fIdx) => {
                    const url = getFileUrl(file);
                    return (
                      <Group key={fIdx} spacing="xs" noWrap>
                        <IconFile size={14} />
                        {url ? (
                          <Anchor href={url} target="_blank" size="xs">
                            {file.fileName}
                          </Anchor>
                        ) : (
                          <Text size="xs">{file.fileName}</Text>
                        )}
                        {file.uploadedAt && (
                          <Text size="xs" color="dimmed">
                            {moment(file.uploadedAt).format("YYYY-MM-DD HH:mm")}
                          </Text>
                        )}
                      </Group>
                    );
                  })}
                </Stack>
              )}
            </Paper>
          );
        }

        if (inputType === "TEXTAREA") {
          return (
            <Textarea
              key={idx}
              label={label}
              size="xs"
              minRows={3}
              value={field.value ?? ""}
              onChange={(e) => updateField(idx, { value: e.target.value })}
            />
          );
        }

        if (isNumberFieldType(inputType)) {
          const precision = getStepFieldNumberPrecision(field);
          return (
            <NumberInput
              key={idx}
              label={label}
              size="xs"
              value={field.value ?? ""}
              precision={precision}
              hideControls
              formatter={formatter.numberFormat}
              parser={formatter.numberParser}
              onChange={(v) => updateField(idx, { value: v })}
            />
          );
        }

        if (inputType === "DATE") {
          return (
            <DatePicker
              key={idx}
              label={label}
              size="xs"
              {...datePickerProps}
              value={parseDateValue(field.value)}
              onChange={(d) =>
                updateField(idx, {
                  value: d ? moment(d).format("YYYY-MM-DD") : null,
                })
              }
            />
          );
        }

        if (inputType === "DATETIME") {
          const dateVal = parseDateValue(field.value);
          return (
            <Stack key={idx} spacing="xs">
              <DatePicker
                label={label}
                size="xs"
                {...datePickerProps}
                value={dateVal}
                onChange={(d) => {
                  if (!d) {
                    updateField(idx, { value: null });
                    return;
                  }
                  const base = dateVal ?? new Date();
                  const merged = new Date(d);
                  merged.setHours(base.getHours(), base.getMinutes(), 0, 0);
                  updateField(idx, { value: merged.toISOString() });
                }}
              />
              <TimeInput
                label="Time"
                size="xs"
                value={dateVal}
                onChange={(d) => {
                  if (!d) return;
                  const base = dateVal ?? new Date();
                  const merged = new Date(base);
                  merged.setHours(d.getHours(), d.getMinutes(), 0, 0);
                  updateField(idx, { value: merged.toISOString() });
                }}
              />
            </Stack>
          );
        }

        return (
          <TextInput
            key={idx}
            label={label}
            size="xs"
            value={field.value ?? ""}
            onChange={(e) => updateField(idx, { value: e.target.value })}
          />
        );
      })}
    </Stack>
  );
};

export default McaStepFieldsInput;
